import { useMemo } from "react"

import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential } from "@l2r-front/l2r-networks"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { TRAFFIC_API_VALUES } from "../../constants/traffic"
import { useSpeedingsStateContext } from "../../contexts/SpeedingsContext"
import { useTraffics } from "../../hooks/queries/useTraffic"

export function RoadTrafficSource(props) {
    const {
        currentActiveLayer,
        road,
        ...sourceProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setError: setMapError } = useMapDispatchContext()
    const { currentYear } = useSpeedingsStateContext()

    const { data: traffics, isLoading: isLoadingTraffics } = useTraffics({
        as_geojson: true,
        linear_location_road: road,
        year: currentYear,
    }, {
        enabled: !!road && !!currentYear,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.trafficSource.error"),
    })

    const { data: referential, isLoading: isLoadingReferential } = useNetworkReferential({
        as_geojson: true,
        linear_location_road: road,
    }, {
        enabled: !!road,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.trafficSource.error"),
    })

    // Need to copy id into properties because of that mapbox issue: 
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if (isLoadingTraffics) {
            return null
        }

        if ((!traffics?.features?.length) && (isLoadingReferential || !referential?.features?.length)) {
            return null
        }

        (referential?.features || []).forEach(feature => {
            const featureExists = traffics.features.some(speedingsFeature =>
                !!speedingsFeature?.linear_location &&
                speedingsFeature.linear_location.road === feature.linear_location[0].road
                && speedingsFeature.linear_location.start === feature.linear_location[0].start
                && speedingsFeature.linear_location.end === feature.linear_location[0].end)

            if (!featureExists) {
                traffics.features.push(feature)
            }
        })

        const newData = sanitizeData(traffics?.features.length ? traffics : referential ?? null)
        if (traffics?.features.length) {
            const newFeatures = []
            if (newData) {
                newData.features.reduce((acc, feature) => {
                    const trafficApiValues = TRAFFIC_API_VALUES[currentActiveLayer]
                    const positiveValue = feature.properties.trafficDirections?.["+"]?.[trafficApiValues]
                    const negativeValue = feature.properties.trafficDirections?.["-"]?.[trafficApiValues]

                    if (positiveValue !== undefined) {
                        acc.push({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                [currentActiveLayer]: positiveValue,
                                side: "RIGHT",
                            },
                        })
                    }
                    if (negativeValue !== undefined) {
                        acc.push({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                [currentActiveLayer]: negativeValue,
                                side: "LEFT",
                            },
                            geometry: {
                                ...feature.geometry,
                                coordinates: feature.geometry.coordinates.toReversed(),
                            },
                        })
                    }
                    if (positiveValue === undefined && negativeValue === undefined) {
                        acc.push(feature)
                    }

                    return acc
                }, newFeatures)
                newData.features = newFeatures
            }
        }
        return newData
    }, [currentActiveLayer, isLoadingReferential, isLoadingTraffics, referential, traffics])

    return (
        <Source {...sourceProps} type="geojson" data={sanitizedData} tolerance={.001} />
    )
}

RoadTrafficSource.propTypes = Source.propTypes